import { pluralEn } from '../../plural';
import type { store as ruStore } from '../ru/store';

export const store: typeof ruStore = {
  navTitle: 'Store',
  pageTitle: {
    terms: 'Delivery and refund terms',
    order: 'Order'
  },
  shop: {
    kicker: 'Official store',
    title: 'Ratten Run Store',
    subtitle: 'Your own lobbies, VIP and coins for Ratten Run. One-time payment, no auto-renewal — your code arrives by email right after payment.',
    loading: 'Loading catalog…',
    loadFailed: 'Could not load products',
    emptyCatalog: 'The catalog is empty for now',
    categoriesAria: 'Catalog sections',
    categories: {
      host: {
        title: 'Your own lobbies',
        description: 'Create your own lobbies and play with friends. The period starts when you activate the code.'
      },
      supporter: {
        title: 'VIP subscription',
        description: 'More match rewards and VIP features in-game. Enabled automatically for your game UID.'
      },
      currency: {
        title: 'Coins',
        description: 'Currency for the Ratten Run in-game shop. The activation code arrives right after payment.'
      },
      tag: {
        title: 'Custom tag',
        description: 'Your own tag next to your name in matches: custom text, colors and icon. One purchase, yours forever.'
      }
    },
    badge: {
      popular: 'Popular',
      bestValue: 'Best value'
    },
    coinBonus: {
      badge: (percent: number): string => `+${percent}%`,
      hint: (percent: number): string => `${percent}% more coins per ruble`
    },
    accessDays: (days: number): string => `${days} ${pluralEn(days, 'day', 'days')} · no auto-renewal`,
    coins: (coins: string): string => `${coins} coins · activation code`,
    trust: {
      label: 'Purchase guarantees',
      payment: {
        title: 'Secure payment',
        text: 'Payment is made on the provider’s secure page — we never see or store your card details.'
      },
      delivery: {
        title: 'Instant delivery',
        text: 'Your code appears on the order page and arrives by email.'
      },
      deliveryModeration: {
        title: 'Moderation after payment',
        text: 'A moderator reviews the tag; the status is shown on the order page. Once approved, the tag appears in your games.'
      },
      support: {
        title: 'Support',
        text: 'If anything goes wrong, email us at'
      }
    },
    custom: {
      amountLabel: 'Number of coins',
      rangeHint: (min: string, max: string, step: string): string =>
        `From ${min} to ${max} coins, in multiples of ${step}`,
      invalid: (min: string, max: string, step: string): string =>
        `Enter an amount from ${min} to ${max} in multiples of ${step}`,
      quick: 'Quick pick',
      calculating: 'Calculating price…',
      unavailable: 'Custom amount is temporarily unavailable',
      quoteFailed: 'Could not calculate the price. Please re-enter the amount.',
      perCoin: (price: string): string => `≈ ${price} per coin`,
      orderName: (coins: string): string => `${coins} coins`
    },
    term: {
      groupAria: 'Subscription term',
      months: (months: number): string => `${months} mo.`,
      discountBadge: (percent: string): string => `−${percent}%`,
      discountHint: (percent: string): string => `${percent}% off compared to paying month by month`,
      perMonth: (price: string): string => `≈ ${price} / mo.`,
      orderName: (name: string, months: number): string => `${name} — ${months} mo.`
    }
  },
  checkout: {
    title: 'Checkout',
    selectedLabel: 'Your selection',
    nothingSelected: 'Choose a product from the catalog',
    uidLabel: 'Game UID',
    uidPlaceholder: 'For example, 42917730',
    uidFromAccount: 'Filled in from your profile',
    emailLabel: 'Email for your activation key',
    emailLabelSupporter: 'Email for confirmation',
    accessKeyLabel: 'Access key',
    accessKeyPlaceholder: 'Enter the access key',
    emailPlaceholder: 'you@example.com',
    paymentMethod: 'Payment method',
    paymentMethodHints: {
      platega: 'SBP (Faster Payments), crypto',
      funpay: 'RU and BY cards, SBP, ERIP, USDT TRC20, Litecoin'
    },
    paymentSoonTitle: 'Online payment will be available soon',
    paymentSoonText: 'You can place an order right now — payment will be marked manually.',
    consentPrefix: 'I accept the',
    offerLink: 'public offer',
    consentAnd: 'and',
    termsLink: 'delivery and refund terms',
    consentPrivacy: ', and consent to personal data processing under the',
    privacyLink: 'privacy policy',
    submit: 'Place order',
    submitting: 'Creating order…',
    priceUnknown: 'enter an amount',
    instructions: {
      host: 'After payment, your code appears on the order page and is sent to your email. Activate it on the Ratten Run Discord server: in the #hoster-access channel, click "Activate hoster key" and paste the code. The period starts at activation.',
      supporter: 'VIP is enabled automatically for the specified UID right after payment — nothing to activate. A confirmation is sent to your email.',
      currency: 'After payment, your code appears on the order page and is sent to your email. Activate it in-game: open the shop (top-left button) and enter the code in the "Promo code" field at the top right. No UID needed.',
      tag: 'After payment the tag goes to moderation and then appears in your game. A moderator may message you using the contact you gave if the tag needs a fix.'
    },
    errors: {
      terms: 'Accept the offer terms and consent to personal data processing.',
      uid: 'UID must contain 1 to 20 digits.',
      email: 'Enter a valid email address for purchase confirmation.',
      createFailed: 'Could not create the order',
      retryHint: 'It is safe to try again.',
      PRODUCT_NOT_FOUND: 'This product is unavailable. Refresh the page and choose another.',
      PLAYER_NOT_FOUND: 'No player with this UID was found. Check the UID.',
      REQUEST_ID_CONFLICT: 'The order details changed. Please place the order again.',
      INVALID_EMAIL: 'Enter a valid email address.',
      INVALID_UID: 'UID must contain 1 to 20 digits.',
      STORE_ACCESS_DENIED: 'Invalid access key.',
      INVALID_ACCESS_KEY: 'Invalid access key.',
      STORE_UNAVAILABLE: 'The store is temporarily unavailable. Try again later.',
      PAYMENT_METHOD_REQUIRED: 'Choose a payment method.',
      PAYMENT_METHOD_UNAVAILABLE: 'The selected payment method is currently unavailable. Choose another.',
      INVALID_PAYMENT_METHOD: 'Invalid payment method. Choose another.',
      PAYMENT_CREATE_FAILED: 'Could not create the payment. Please try again.',
      INVALID_REQUEST: 'Invalid request. Refresh the page and try again.',
      CUSTOM_COINS_UNAVAILABLE: 'Custom coin amount is temporarily unavailable. Choose a ready-made pack.',
      INVALID_COINS: 'Invalid number of coins. Check the range and step.',
      NOT_CUSTOM_PRODUCT: 'This product does not support a custom amount. Refresh the page.',
      AMOUNT_EXCEEDS_LIMIT: 'The order total exceeds 10,000 ₽. Choose a smaller amount.',
      TAG_REQUIRED: 'This product needs a tag design. Refresh the page.',
      TAG_NOT_ALLOWED: 'A tag cannot be set for this product. Refresh the page.',
      AUTH_REQUIRED: 'Sign in with Steam: the tag is linked to your game account.',
      INVALID_TAG_NAME: 'Invalid tag text: 2–20 characters, letters, digits, spaces and _ . - !',
      INVALID_TAG_COLORS: 'Invalid tag colors: 1 to 6 colors are required.',
      INVALID_TAG_ANGLE: 'Invalid gradient direction.',
      INVALID_TAG_ICON: 'The icon was rejected. Upload another image or remove the icon.',
      CONTACT_REQUIRED: 'Tell us how to reach you (at least 2 characters).',
      CONTACT_TOO_LONG: 'The contact is too long (500 characters max).',
      INVALID_CONTACT: 'The contact contains invalid hidden characters. Retype it manually.',
      PLAYER_REQUIRED: 'No game profile is linked to your account. Play at least one Ratten Run match and try again.',
      TOO_MANY_PENDING_TAG_ORDERS: 'Too many unpaid tag orders. Pay the earlier ones or try again in a day.',
      ICON_STORE_FAILED: 'Could not save the icon. Try again later or order the tag without an icon.',
      INVALID_TERM: 'This subscription term is unavailable. Refresh the page and pick another.',
      TERM_NOT_ALLOWED: 'This product has no term choice. Refresh the page.'
    }
  },
  myOrders: {
    title: 'My orders',
    loading: 'Loading orders…',
    loadFailed: 'Could not load orders',
    empty: 'You have no orders yet.',
    open: 'Open',
    openAria: (name: string): string => `Open order: ${name}`,
    prev: 'Back',
    next: 'Next',
    summary: (page: number, total: number): string => `Page ${page} of ${total}`
  },
  status: {
    test: 'Test',
    order: {
      pending: 'Awaiting payment',
      paid: 'Paid',
      canceled: 'Canceled'
    },
    fulfillment: {
      pending: 'Delivery pending',
      completed: 'Delivered',
      failed: 'Delivery failed'
    },
    email: {
      pending: 'Email queued',
      sent: 'Email sent',
      failed: 'Email failed',
      skipped: 'Email not sent'
    }
  },
  errorCodes: {
    FULFILLMENT_RETRYING: 'Delivery is delayed; we are retrying automatically.',
    FULFILLMENT_FAILED: 'We could not deliver the product automatically. You do not need to pay again.'
  },
  order: {
    badge: 'Order',
    numberLabel: 'Order number',
    copyOrderId: 'Copy order number',
    checkNow: 'Check now',
    backToStore: 'Back to store',
    contactSupport: 'Contact support',
    checkingTitle: 'Checking your order',
    checkingHint: 'This usually takes a few seconds. Do not close this page.',
    unavailableTitle: 'Order unavailable',
    accessMissing: 'Order access data was not found. Open the link from your email, or the order on the device where you placed it.',
    checkFailed: 'Could not check the order',
    notFound: 'Order not found. Check the link.',
    updateFailed: 'Could not update status:',
    pendingTitle: 'Waiting for payment',
    pendingText: 'The order is created and waiting for payment confirmation. This page will update automatically.',
    pendingPaymentLink: 'Go to payment',
    activatingTitle: 'Payment confirmed, delivering your purchase',
    activatingText: 'Delivery is not complete yet — this page will update automatically.',
    failedTitle: 'Could not deliver the purchase',
    failedText: 'You do not need to pay again. Save the order number and contact support for a manual review.',
    canceledTitle: 'Order canceled',
    canceledText: 'The payment was not completed. If your bank shows a charge, contact support with your order number.',
    hostReadyTitle: 'Your hosting code is ready',
    hostCodeLabel: 'Your hosting code',
    currencyReadyTitle: 'Your promo code is ready',
    currencyCodeLabel: 'Your promo code',
    activation: {
      host: {
        title: 'How to activate',
        steps: [
          'Join the Ratten Run Discord server.',
          'Open the #hoster-access channel.',
          'Click "Activate hoster key" and paste your code.'
        ],
        note: 'The access period starts at activation.',
        openDiscord: 'Open Discord'
      },
      currency: {
        title: 'How to activate',
        steps: [
          'Join a Ratten Run match.',
          'Open the shop: the button is in the top-left corner of the screen.',
          'In the top-right corner of the shop, enter the code in the "Promo code" field and click "Activate".'
        ],
        note: 'Coins are credited to your game account right after activation.'
      }
    },
    subscriptionTitle: 'Subscription activated',
    subscriptionUntil: 'Subscription active until',
    subscriptionText: 'Your VIP subscription has been enabled or extended automatically. Nothing to activate.',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy. Select the text and copy it manually.',
    emailSent: 'A copy was sent by email',
    emailPending: 'Sending a copy by email…',
    emailFailed: 'The email could not be sent — save the code from this page.',
    longWait: 'If the status does not change for a while, save the order number and contact support.',
    funpay: {
      title: 'Pay for your order on FunPay',
      lead: 'Payment happens on FunPay. The order code lets the seller match your purchase to this order.',
      codeLabel: 'Order code',
      codeBadge: 'Send this exact code to the seller',
      codeHint: 'The seller needs exactly this code, it starts with RR-. Do not send the order number from the bottom of the page.',
      stepsLabel: 'How to pay',
      step1: 'Buy the lot on FunPay.',
      openLot: 'Open the lot on FunPay',
      step2: 'Right after buying, send the RR-… code to the seller in the FunPay chat.',
      step3: 'Wait for delivery: this page updates by itself.'
    },
    uidLine: (product: string, uid: string): string => `${product} · UID ${uid}`
  },
  terms: {
    back: '← Back to home',
    heading: 'Delivery and refund terms',
    sections: [
      {
        title: 'What we sell',
        paragraphs: [
          'The store sells digital services and goods for the Ratten Run custom game in Dota 2: hosting access for your own lobbies for 7, 30 or 90 days, a 1-month VIP subscription, and in-game currency packs. Contents, duration and price in rubles are shown on the product card before payment. Purchases are one-time, with no automatic renewal or recurring charges.'
        ]
      },
      {
        title: 'Payment',
        paragraphs: [
          "Payment is made once by bank card or another method available on the payment service's secure page. Card details are entered only on the payment service side and are never passed to the store. A receipt is issued after payment."
        ]
      },
      {
        title: 'Delivery methods and timing',
        paragraphs: [
          'All products are delivered electronically only, free of charge. There is no physical delivery.',
          'Hosting: the access code is shown on the order page right after payment confirmation (usually within a few minutes) and is also sent to your email. Activate it on the Ratten Run Discord server: in the #hoster-access channel, click "Activate hoster key" and paste the code; the access period starts at activation.',
          'In-game currency: the key is shown on the order page right after payment confirmation and is also sent to your email. Redeem it inside the Ratten Run game.',
          'VIP subscription: activated automatically for the specified game UID after payment confirmation, within 24 hours. A confirmation is sent to your email.'
        ]
      },
      {
        title: 'Cancellation and refunds',
        paragraphs: [
          'You may cancel a digital product or service of proper quality and get a full refund within 7 days, not counting the day of purchase, without giving a reason.',
          'You may cancel hosting or a subscription at any time during the paid period and get a refund for the unused part.',
          'If a code or key was not issued, does not work, or does not deliver what you paid for, we will replace it or refund you in full.'
        ]
      }
    ],
    refundHowTitle: 'How to request a refund',
    refundHowIntro: 'Email ',
    refundHowDetails: '. Include your order number, date, amount and request. Do not send your activation key, bank card details, or order access link.',
    refundTiming: 'Requests are reviewed within 10 calendar days of receipt. Refunds are made using the original payment method (to the same bank card or account); the time to credit depends on your bank.',
    offerNote: 'Full terms are set out in the ',
    offerLink: 'public offer',
    moderationCode: (code: string): string => `Approval code: ${code}`
  },
  admin: {
    kicker: 'Admin',
    title: 'Store',
    tabsAria: 'Store sections',
    tabs: {
      orders: 'Orders',
      analytics: 'Analytics',
      products: 'Products',
      categories: 'Categories',
      payments: 'Payment methods',
      tags: 'Tags'
    },
    common: {
      loading: 'Loading…',
      retry: 'Retry',
      all: 'All',
      cancel: 'Cancel',
      save: 'Save',
      page: (page: number, total: number): string => `Page ${page} of ${total}`,
      total: (count: string): string => `Total: ${count}`,
      invalidRange: 'The start date must be before the end date',
      utcHint: 'Dates are in UTC',
      prev: 'Back',
      next: 'Next'
    },
    apiErrors: {
      INVALID_FUNPAY_LOT_ID: 'FunPay lot ID: digits only, 1 to 20.',
      FUNPAY_LOT_TAKEN: 'This FunPay lot is already linked to another product or term.',
      FUNPAY_LOT_NOT_ALLOWED: 'A FunPay lot cannot be set for a custom-amount product: it has no fixed price.',
      INVALID_FUNPAY_LOT_TITLE: 'Lot title must be up to 255 characters.',
      FUNPAY_ORDER_ALREADY_USED: 'This FunPay order is already linked to another store order.',
      generic: 'Could not complete the action',
      ORDER_CANCELED: 'The order is already canceled.',
      ORDER_NOT_CANCELABLE: 'The order cannot be canceled: it is already paid or canceled.',
      NOTHING_TO_RETRY: 'Nothing to retry: delivery and email are not in a failed state.',
      ORDER_NOT_FOUND: 'Order not found.',
      COMMENT_REQUIRED: 'Enter a comment.',
      COMMENT_TOO_LONG: 'The comment is too long (at most 500 characters).',
      STORE_UNAVAILABLE: 'The store is temporarily unavailable. Try again later.',
      INVALID_REQUEST: 'Invalid request.',
      INVALID_PERIOD: 'Invalid period: the start date must be before the end date.',
      INVALID_FILTER: 'Invalid filter parameters.',
      NAME_REQUIRED: 'Enter a name.',
      NAME_TOO_LONG: 'The name is too long (at most 255 characters).',
      DESCRIPTION_REQUIRED: 'Enter a description.',
      DESCRIPTION_TOO_LONG: 'The description is too long.',
      FEATURE_BLANK: 'A feature cannot be empty.',
      FEATURES_REQUIRED: 'Provide a feature list (may be empty).',
      PRODUCT_ID_REQUIRED: 'Enter a product ID.',
      PRODUCT_ID_TOO_LONG: 'Product ID is too long.',
      INVALID_PRODUCT_ID: 'Product ID: lowercase latin letters, digits and hyphens, 3–64 characters.',
      PRICE_REQUIRED: 'Enter a price.',
      CATEGORY_REQUIRED: 'Choose a category.',
      CURRENCY_REQUIRED: 'Enter a currency.',
      INVALID_CURRENCY: 'Only RUB is supported.',
      INVALID_IMAGE_URL: 'Invalid image. Upload the file again.',
      FEATURE_TOO_LONG: 'A feature is too long (at most 255 characters).',
      TOO_MANY_FEATURES: 'Too many features (at most 20).',
      INVALID_PRICE: 'Invalid price: greater than zero, at most two decimal places.',
      PRICE_EXCEEDS_LIMIT: 'The price exceeds the payment provider limit (10,000 ₽).',
      INVALID_USD_PRICE: 'Invalid USD price: greater than zero, at most two decimal places.',
      INVALID_FULFILLMENT_VALUE: 'Invalid delivery value.',
      INVALID_BADGE: 'Invalid badge.',
      PRODUCT_NOT_FOUND: 'Product not found.',
      PAYMENT_METHOD_NOT_CONFIGURED: 'Payment method secrets are not configured',
      PAYMENT_METHOD_NOT_FOUND: 'Payment method not found.',
      ENABLED_REQUIRED: 'Specify whether to enable or disable the payment method.',
      PRODUCT_ID_TAKEN: 'A product with this ID already exists.',
      STORE_CATEGORY_NOT_FOUND: 'Category not found.',
      TITLE_RU_REQUIRED: 'Enter a title (RU).',
      TITLE_RU_TOO_LONG: 'The title (RU) is too long (at most 64 characters).',
      TITLE_EN_REQUIRED: 'Enter a title (EN).',
      TITLE_EN_TOO_LONG: 'The title (EN) is too long (at most 64 characters).',
      DESCRIPTION_RU_REQUIRED: 'Provide a description (RU), may be empty.',
      DESCRIPTION_RU_TOO_LONG: 'The description (RU) is too long (at most 500 characters).',
      DESCRIPTION_EN_REQUIRED: 'Provide a description (EN), may be empty.',
      DESCRIPTION_EN_TOO_LONG: 'The description (EN) is too long (at most 500 characters).',
      SORT_ORDER_REQUIRED: 'Enter a sort order.',
      SORT_ORDER_INVALID: 'Sort order must be an integer from 0 to 1000.',
      TAG_REQUEST_NOT_FOUND: 'Tag request not found.',
      TAG_REQUEST_NOT_PENDING: 'The request is already processed: the action is only available for tags under review.',
      INVALID_TAG_NAME: 'Invalid tag text: 2–20 characters, letters, digits, spaces and _ . - !',
      INVALID_TAG_COLORS: 'Invalid tag colors: 1 to 6 colors are required.',
      INVALID_TAG_ANGLE: 'Invalid gradient direction.',
      INVALID_TAG_ICON: 'The icon was rejected. Upload another image.',
      ICON_STORE_FAILED: 'Could not save the icon. Try again later.',
      PLAYER_NOT_FOUND: 'The request\'s player was not found: there is no one to assign the tag to.',
      TERMS_NOT_ALLOWED: 'Subscription terms can only be set on a product in the Subscription category.',
      DUPLICATE_TERM: 'Terms must not repeat.',
      INVALID_TERM_MONTHS: 'A term is a whole number of months from 2 to 24.',
      TERM_DISCOUNT_TOO_HIGH: 'A term discount cannot exceed the product\'s maximum discount.',
      INVALID_TERM_MAX_DISCOUNT: 'Maximum discount is a whole number from 0 to 50.'
    },
    orders: {
      filters: {
        status: 'Status',
        testType: 'Order type',
        testRegular: 'Regular',
        testOnly: 'Test',
        fulfillment: 'Delivery',
        product: 'Product',
        query: 'Search',
        queryPlaceholder: 'Email, UID or order number',
        from: 'From date',
        to: 'To date',
        apply: 'Apply',
        reset: 'Reset'
      },
      table: {
        created: 'Created',
        order: 'Order',
        customer: 'Customer',
        product: 'Product',
        amount: 'Amount',
        status: 'Status',
        fulfillment: 'Delivery',
        email: 'Email',
        actions: 'Actions'
      },
      uid: (uid: string): string => `UID ${uid}`,
      account: 'Account',
      attempts: (count: number): string => `Delivery attempts: ${count}`,
      paidBy: (name: string, provider: string): string => `Marked paid by: ${name} (${provider})`,
      provider: (provider: string): string => `Payment method: ${provider}`,
      paymentCode: (code: string): string => `Payment code: ${code}`,
      funpayOrder: (id: string): string => `FunPay #${id}`,
      funpayOrderAria: (id: string): string => `Open FunPay order ${id} on FunPay`,
      disputeBadge: { dispute: 'FunPay dispute', refund: 'FunPay refund' },
      disputeAt: (date: string): string => `Since ${date}`,
      paidComment: (comment: string): string => `Comment: ${comment}`,
      empty: 'No orders match the selected filters',
      loadFailed: 'Could not load orders',
      actionFailed: 'Could not complete the action',
      markPaid: 'Mark as paid',
      markPaidAria: (id: string): string => `Mark order ${id} as paid`,
      retry: 'Retry delivery',
      retryAria: (id: string): string => `Retry delivery of order ${id}`,
      cancel: 'Cancel order',
      cancelAria: (id: string): string => `Cancel order ${id}`,
      markPaidDialog: {
        title: 'Mark order as paid',
        description: 'The order will become "Paid" and will be delivered to the customer. The action is recorded in the audit log.',
        commentLabel: 'Comment (required)',
        commentPlaceholder: 'For example: card transfer, receipt #123',
        funpayHint: 'For FunPay payments enter "FunPay #<order number>": the number is linked to the order and prevents delivering twice.',
        commentRequired: 'Enter a comment',
        commentTooLong: 'At most 500 characters',
        confirm: 'Mark as paid',
        saving: 'Saving…'
      },
      cancelDialog: {
        title: 'Cancel order',
        description: 'The order will be canceled. This cannot be undone.',
        confirm: 'Cancel order',
        back: 'Keep order'
      },
      toasts: {
        markedPaid: 'Order marked as paid',
        retried: 'Delivery queued',
        canceled: 'Order canceled'
      }
    },
    analytics: {
      includeTest: 'Include test orders',
      from: 'From date',
      to: 'To date',
      apply: 'Show',
      paidCount: 'Paid orders',
      revenue: 'Revenue',
      noRevenue: 'No payments in this period',
      byProduct: 'By product',
      byDay: 'By day',
      colProduct: 'Product',
      colDate: 'Date',
      colCount: 'Orders',
      colAmount: 'Amount',
      empty: 'No paid orders in this period',
      loadFailed: 'Could not load analytics',
      barAria: (date: string, count: number, amount: string): string => `${date}: ${count} orders, ${amount}`
    },
    payments: {
      loadFailed: 'Could not load payment methods',
      empty: 'No payment methods yet',
      table: {
        method: 'Method',
        id: 'ID',
        configured: 'Secrets',
        status: 'Status',
        enabled: 'Enabled'
      },
      configuredYes: 'Configured',
      configuredNo: 'Not configured',
      statusOn: 'Accepting payments',
      statusOff: 'Off',
      adminOnly: 'Admins only',
      notConfiguredHint: 'Secrets are not configured: the method cannot be enabled until they are set in the BE environment.',
      toggleAria: (name: string): string => `Enable payment method ${name}`,
      updated: (name: string, enabled: boolean): string => `${name}: ${enabled ? 'enabled' : 'disabled'}`
    },
    products: {
      create: 'New product',
      loadFailed: 'Could not load products',
      empty: 'No products yet',
      table: {
        sort: 'Order',
        id: 'ID',
        name: 'Name',
        category: 'Category',
        image: 'Image',
        price: 'Price',
        value: 'Delivery',
        status: 'Status',
        actions: 'Actions'
      },
      edit: 'Edit',
      editAria: (id: string): string => `Edit product ${id}`,
      active: 'Active',
      inactive: 'Hidden',
      categories: {
        host: 'Host',
        supporter: 'Subscription',
        currency: 'Currency',
        tag: 'Tag'
      },
      valueDays: (days: number): string => `${days} d`,
      valueCustom: 'Custom amount',
      valueCoins: (coins: string): string => `${coins} coins`,
      dialog: {
        createTitle: 'New product',
        editTitle: 'Edit product',
        editHint: 'Changes do not affect orders that already exist.',
        id: 'ID (slug)',
        idHint: 'Lowercase Latin letters, digits and hyphens, 3-64 characters',
        lockedHint: 'Category and ID of an existing product cannot be changed: they define how it is delivered. Create a new product and hide the old one.',
        category: 'Category',
        name: 'Name',
        description: 'Description',
        nameEn: 'Name (EN)',
        descriptionEn: 'Description (EN)',
        featuresEn: 'Features (EN)',
        features: 'Features',
        featuresHint: 'One per line',
        enHint: 'If left empty, the Russian text is shown in English',
        price: 'Price, RUB',
        usdPrice: 'Price, USD (optional)',
        valueDays: 'Duration, days',
        valueCoins: 'Coins',
        badge: 'Badge',
        badgeNone: 'No badge',
        badgePopular: 'Popular',
        badgeBestValue: 'Best value',
        sortOrder: 'Sort order',
        featured: 'Highlight and preselect',
        active: 'Show in the store',
        saving: 'Saving…',
        image: 'Image',
        imageHint: 'PNG, JPG or WebP, up to 5 MB',
        funpayLot: 'FunPay lot',
        funpayLotHint: 'Lot ID from the FunPay link (digits only). Empty means FunPay payment is unavailable for this product.',
        funpayLotTitle: 'FunPay lot title',
        funpayLotTitleHint: 'As in the lot headline: the bot uses it to match orders.',
        imagePreviewAlt: 'Product image preview',
        imageUpload: 'Upload',
        imageReplace: 'Replace',
        imageRemove: 'Remove',
        imageUploading: 'Uploading…',
        customHint: 'Custom-amount product: the server prices the chosen amount; no fulfillment amount or duration is set.',
        terms: {
          title: 'Subscription terms',
          hint: 'The base term is 1 month: its price and duration are set above. Extra terms go here; one month = 30 days.',
          maxDiscount: 'Max discount, %',
          maxDiscountHint: '0 to 50. The cap for both the automatic calculation and manual discounts.',
          empty: 'No extra terms: the product has a single term.',
          months: 'Months',
          discount: 'Discount, %',
          discountPlaceholder: 'auto',
          discountHint: 'Empty: the discount is calculated automatically.',
          funpayLot: 'FunPay lot (ID)',
          funpayLotTitle: 'FunPay lot title',
          funpayHint: 'Term FunPay lot: ID from the link (digits only) and the title as in the lot headline. Empty means FunPay payment is unavailable for this term.',
          active: 'Visible',
          add: 'Add term',
          remove: 'Remove',
          removeAria: (months: string): string => `Remove the ${months} mo. term`,
          rowAria: (months: string): string => `${months} mo. term`,
          previewLabel: 'Calculated',
          preview: (price: string, discount: string, monthly: string): string => `${price} · ${discount}% off · ≈ ${monthly} / mo.`,
          previewEmpty: 'Enter a valid term'
        }
      },
      errors: {
        imageTooLarge: 'The file is larger than 5 MB',
        imageUploadFailed: 'Could not upload the image',
        id: 'ID: 3-64 characters, a-z, 0-9 and hyphen',
        name: 'Enter a name',
        price: 'Price must be greater than zero',
        usdPrice: 'USD price must be greater than zero',
        value: 'Delivery value must be an integer greater than zero',
        sortOrder: 'Sort order must be an integer',
        description: 'Enter a description',
        priceScale: 'Price: greater than zero, at most two decimal places',
        usdScale: 'USD price: greater than zero, at most two decimal places',
        nameLong: 'Name must be at most 255 characters',
        nameEnLong: 'Name (EN) must be at most 255 characters',
        descriptionEnLong: 'Description (EN) must be at most 5000 characters',
        featuresCount: 'At most 20 features',
        featureLong: 'Each feature must be at most 255 characters',
        funpayLot: 'FunPay lot: digits only, 1 to 20',
        funpayLotTitleLong: 'Lot title must be up to 255 characters',
        termMax: 'Max discount: a whole number from 0 to 50',
        termMonths: 'Subscription term: a whole number of months from 2 to 24',
        termDuplicate: 'Subscription terms must not repeat',
        termDiscount: 'Term discount: a number from 0 to the max discount, at most two decimal places',
        duplicate: 'A product with this ID already exists',
        save: 'Could not save the product'
      },
      toasts: {
        created: 'Product created',
        updated: 'Product updated'
      }
    },
    categories: {
      loadFailed: 'Failed to load categories',
      empty: 'No categories yet',
      hint: 'Category titles and descriptions are shown in the storefront. The set of categories is fixed.',
      table: {
        sort: 'Order',
        id: 'ID',
        title: 'Title',
        description: 'Description',
        actions: 'Actions'
      },
      edit: 'Edit',
      editAria: (id: string): string => `Edit category ${id}`,
      dialog: {
        title: 'Edit category',
        titleRu: 'Title (RU)',
        titleEn: 'Title (EN)',
        descriptionRu: 'Description (RU)',
        descriptionEn: 'Description (EN)',
        sortOrder: 'Order (0–1000)',
        saving: 'Saving…'
      },
      errors: {
        title: 'Enter titles in both languages (up to 64 characters)',
        description: 'Description must be up to 500 characters',
        sortOrder: 'Order must be an integer from 0 to 1000'
      },
      toasts: {
        updated: 'Category updated'
      }
    }
  }
};
